<template>
  <div class="main-infoSheet">
    <f7-sheet
      :opened="opened"
      bottom
      close-by-outside-click
      @sheet:closed="emit('closed')"
    >
      <div class="content-control-info header">
        <slot name="header" />
      </div>
      <div class="content-control-info">
        <div class="content-control-item">
          <f7-block class="content-user-info no-margin-left">
            <div class="user-info-item padding-left-half">
              <img
                v-if="avatar"
                class="margin-top-half"
                width="58"
                height="58"
                :src="avatarImg"
                @error="hasImgError = true"
              >

              <div class="item-title-row">
                <div class="item-title-bold">
                  {{ title }}
                </div>
                <div
                  v-if="subtitle1"
                  class="item-subtitle1"
                >
                  {{ subtitle1 }}
                </div>
                <div class="item-subtitle">
                  {{ subtitle }}
                </div>
              </div>
            </div>

            <div
              v-if="issueReason && issueReason.length > 0"
              class="user-info-item"
              :style="{ color: issueColor }"
            >
              <span>
                <img
                  :src="cautionImage"
                  class="issue-icon"
                ></span>
              {{ issueReason }}
            </div>
          </f7-block>
        </div>
        <div
          v-if="showIncidenceImage"
          class="content-control-item"
          :style="{ background: selectedStateBgColor }"
        >
          <img
            width="50vw"
            :src="stateImg"
          >
          <span class="modal-text text-align-center">{{ stateText }}</span>
        </div>
        <div
          v-else-if="link"
          class="content-control-item"
        >
          <img
            width="12px"
            :src="nextImg"
          >
        </div>
      </div>
    </f7-sheet>
  </div>
</template>

<script setup>
// @ts-check
import modalIncidenciaRestrictiva from '@/static/icons/ic_stop.svg';
import modalIncidencia from '@/static/icons/ic_warning.svg';
import cautionNoBorder from '@/static/icons/ic_cautionNoBorder.svg';
import imgChecked from '@/static/img/ic_checked.svg';
import imgDefault from '@/static/img/Unknown.svg';
import cautionImage from '@/static/icons/ic_alert.svg';
import modalError from '@/static/img/ic_errorCode.svg';
import nextImg from '@/static/img/ic_next.svg';
import { computed, ref } from 'vue';

/** @import { PropType } from 'vue' */
/** @import { InfoSheetState } from '@erpagroapp/types' */

const props = defineProps({
  opened: { type: Boolean, default: false },
  imageSrc: { type: String, default: null },
  title: { type: String, default: '' },
  subtitle1: { type: [String, Number], default: null },
  subtitle: { type: [String, Number], default: '' },
  stateText: { type: String, default: '' },
  issueReason: { type: String, default: null },
  showIncidenceImage: { type: Boolean, default: true },
  state: {
    type: /** @type {PropType<InfoSheetState>} */ (String),
    default: 'DEFAULT',
  },
  avatar: { type: Boolean, default: false },
  link: { type: String, default: null },
});

const emit = defineEmits(['closed']);

const hasImgError = ref(false);

const stateBgColors = Object.freeze(/** @satisfies {Record<InfoSheetState, string>} */ ({
  DEFAULT: 'var(--his-orange)',
  ENTER: 'var(--his-greenlight)',
  CAUTION: 'var(--his-orange)',
  EXIT: 'var(--his-greendark)',
  ISSUE: 'var(--his-orange)',
  BLOCKING_ISSUE: '#D0021B',
  ERROR: 'var(--his-tomate)',
}));

const stateImgs = Object.freeze(/** @satisfies {Record<InfoSheetState, string>} */ ({
  DEFAULT: modalIncidencia,
  CAUTION: cautionNoBorder,
  ENTER: imgChecked,
  EXIT: imgChecked,
  ISSUE: modalIncidencia,
  BLOCKING_ISSUE: modalIncidenciaRestrictiva,
  ERROR: modalError,
}));

const issueColors = Object.freeze({
  DEFAULT: 'var(--his-orange)',
  ISSUE: '#D0021B',
});

const selectedStateBgColor = computed(() => (Object.keys(stateBgColors).includes(props.state)
  ? stateBgColors[props.state]
  : stateBgColors.DEFAULT));

const issueColor = computed(() => (props.state === 'BLOCKING_ISSUE'
  ? issueColors.ISSUE
  : issueColors.DEFAULT));

const stateImg = computed(() => (Object.keys(stateImgs).includes(props.state)
  ? stateImgs[props.state]
  : stateImgs.DEFAULT));

const avatarImg = computed(() => (!hasImgError.value && props.imageSrc
  ? props.imageSrc
  : imgDefault));
</script>

<style scoped lang="scss">
.sheet-modal-inner .content-control-info {
  display: -webkit-box;
  display: -moz-box;
  display: -ms-flexbox;
  display: -webkit-flex;
  display: flex;
  filter: drop-shadow(0 4px 4px rgb(0 0 0 / 20%));
  -webkit-box-direction: normal;
  -moz-box-direction: normal;
  -webkit-box-orient: horizontal;
  -moz-box-orient: horizontal;
  -webkit-flex-direction: row;
  -ms-flex-direction: row;
  flex-direction: row;
  -webkit-flex-wrap: nowrap;
  -ms-flex-wrap: nowrap;
  flex-wrap: nowrap;
  -webkit-box-pack: justify;
  -moz-box-pack: justify;
  -webkit-justify-content: space-around;
  -ms-flex-pack: distribute;
  justify-content: space-around;
  -webkit-align-content: flex-start;
  -ms-flex-line-pack: start;
  align-content: flex-start;
  -webkit-box-align: stretch;
  -moz-box-align: stretch;
  -webkit-align-items: stretch;
  -ms-flex-align: stretch;
  align-items: stretch;
}

.content-control-item {
  display: flex;
  align-items: center;
}

.content-control-item:nth-child(1) {
  -webkit-box-ordinal-group: 1;
  -moz-box-ordinal-group: 1;
  -webkit-order: 0;
  -ms-flex-order: 0;
  order: 0;
  -webkit-box-flex: 2;
  -moz-box-flex: 2;
  -webkit-flex: 2 1 80%;
  -ms-flex: 2 1 80%;
  flex: 2 1 80%;
  -webkit-align-self: auto;
  -ms-flex-item-align: auto;
  align-self: auto;
  padding: 6px 0 10px;
  border-radius: 3px 0 0;
  -moz-border-radius: 3px 0 0;
  -webkit-border-radius: 3px 0 0;
}

.content-control-item:nth-child(1) .item-title-bold {
  color: var(--his-ajo-shade);
  font-weight: 500;
  font-size: 16px;
}

.item-title-row {
  display: grid;
}

.content-control-item:nth-child(1) .item-subtitle {
  color: #66ba33;
  font-weight: 500;
  text-overflow: ellipsis;
  overflow: hidden;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  margin-right: 1rem;
}

.content-control-item:nth-child(1) .item-subtitle1 {
  color: var(--his-arandano);
  font-weight: 400;
  text-overflow: ellipsis;
  overflow: hidden;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  margin-right: 1rem;
}

.content-control-item:nth-child(2) {
  -webkit-box-ordinal-group: 1;
  -moz-box-ordinal-group: 1;
  -webkit-order: 0;
  -ms-flex-order: 0;
  order: 0;
  -webkit-box-flex: 1;
  -moz-box-flex: 1;
  flex: 1 1 100%;
  -webkit-flex: 1 1 100px;
  -ms-flex: 1 1 100px;
  -webkit-align-self: stretch;
  -ms-flex-item-align: stretch;
  align-self: stretch;
  text-align: center;
  display: flex;
  flex-direction: column;
  padding: 10px;
  min-width: 80px;
  min-height: 85px;
  align-items: center;
  justify-content: center;
  border-radius: 0 3px 0 0;
  -moz-border-radius: 0 3px 0 0;
  -webkit-border-radius: 0 3px 0 0;
}

@-moz-document url-prefix() {
  .content-control-info .sheet-modal-inner {
    width: 100%;
    box-sizing: border-box;
    -moz-box-sizing: border-box;
  }
}

.content-user-info {
  display: -webkit-box;
  display: -moz-box;
  display: -ms-flexbox;
  display: -webkit-flex;
  display: flex;
  -webkit-box-direction: normal;
  -moz-box-direction: normal;
  -webkit-box-orient: horizontal;
  -moz-box-orient: horizontal;
  -webkit-flex-direction: column;
  -ms-flex-direction: column;
  flex-direction: column;
  -webkit-flex-wrap: wrap;
  -ms-flex-wrap: wrap;
  flex-wrap: wrap;
  -webkit-box-pack: justify;
  -moz-box-pack: justify;
  -webkit-justify-content: space-around;
  -ms-flex-pack: distribute;
  justify-content: space-around;
  -webkit-align-content: flex-start;
  -ms-flex-line-pack: start;
  align-content: flex-start;
  -webkit-box-align: stretch;
  -moz-box-align: stretch;
  -webkit-align-items: stretch;
  -ms-flex-align: stretch;
  align-items: stretch;
  padding-left: 10px;
  padding-right: 0;
}

.content-user-info .user-info-item:nth-child(1) {
  -webkit-box-ordinal-group: 1;
  -moz-box-ordinal-group: 1;
  -webkit-order: 0;
  -ms-flex-order: 0;
  order: 0;
  -webkit-box-flex: 1;
  -moz-box-flex: 1;
  -webkit-align-self: auto;
  -ms-flex-item-align: auto;
  align-self: auto;
  -webkit-flex-direction: row;
  -ms-flex-direction: row;
  flex-direction: row;
  display: flex;
  align-items: center;
}

.content-user-info .user-info-item:nth-child(1) img {
  margin: 0 10px 5px 0;
  width: 64px;
  height: 64px;
  max-width: 64px;
  border-radius: 20px;
  -webkit-border-radius: 20px;
  -moz-border-radius: 20px;
  -ms-border-radius: 20px;
  -o-border-radius: 20px;
  object-fit: cover;
}

.content-user-info .user-info-item:nth-child(2) {
  -webkit-box-ordinal-group: 1;
  -moz-box-ordinal-group: 1;
  -webkit-order: 0;
  -ms-flex-order: 0;
  order: 0;
  -webkit-box-flex: 2;
  -moz-box-flex: 2;
  -webkit-align-self: auto;
  -ms-flex-item-align: auto;
  align-self: auto;
}

.content-user-info .user-info-item:nth-child(2) img {
  margin: 0 5px;
}

.content-user-info .user-info-item:nth-child(3) {
  -webkit-box-ordinal-group: 1;
  -moz-box-ordinal-group: 1;
  -webkit-order: 0;
  -ms-flex-order: 0;
  order: 0;
  -webkit-box-flex: 0;
  -moz-box-flex: 0;
  -webkit-flex: 0 1 100%;
  -ms-flex: 0 1 100%;
  flex: 0 1 100%;
  -webkit-align-self: auto;
  -ms-flex-item-align: auto;
  align-self: auto;
}

/*
    Legacy Firefox implementation treats all flex containers
    as inline-block elements.
*/

@-moz-document url-prefix() {
  .content-user-info {
    width: 100%;
    box-sizing: border-box;
    -moz-box-sizing: border-box;
  }
}

.modal-img {
  position: relative;
  width: 111px;
  height: auto;
  text-align: center;
}

.modal-text {
  color: #fff;
  width: 100%;
}

.block-modal {
  padding: 0;
  height: 100%;
  margin: 0;
}

img.issue-icon {
  padding: 2px;
  vertical-align: sub;
}

.content-control-info.header {
  justify-content: flex-start;
}
</style>
